<script setup lang="ts">
// Option C (2026-10-11): the one momentum-fund signal panel. Replaces the
// copy-pasted GoldSignalPanel.vue / GgeSignalPanel.vue; every per-fund
// difference (labels, accent, momentum field, telemetry legs, overlay,
// degraded pills, stats cells, explainer prose) comes from `config`
// (FUND_PAGES[slug].signalPanel — see fundSignalPanels.ts). The rendered
// DOM, testids and styling are those of the two original panels.
//
// Data: the stamped 08:40 decision (+ its display-only live preview) is
// re-polled every 30s; K_t (and the optional official-NAV leg) every 5 min;
// the live synth-NAV hero every 5s via config.useLiveSynthNav. The decision
// degrades gracefully when the Redis key is missing (early boot, pre-cycle
// day, redis outage).
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type CalendarStatus, type FundNavLatest, type GoldKtLatestResponse } from '../../api/client'
import { useStrikeAnchor } from '../../composables/useStrikeAnchor'
import { useHlCutoffCountdown } from '../../composables/useHlCutoffCountdown'
import { formatOverlayPct } from '../../utils/goldOverlay'
import { bps, fmtTimestamp, type FundSignalPanelConfig, type FundSignalPayloadLike, type FundSignalView } from './fundSignalPanel'
import { gateStateText } from './fundSignalFields'

const props = defineProps<{ config: FundSignalPanelConfig }>()
const cfg = props.config
const k = cfg.key

const signal = ref<FundSignalPayloadLike | null>(null)
const errored = ref(false)
const loading = ref(true)

const isAvailable = computed(() =>
  !!signal.value && signal.value.available !== false && !!signal.value.decision)
const decisionLabel = computed(() => signal.value?.decision ?? 'HOLD')
const positionProxy = computed(() => signal.value?.position_proxy ?? null)
const isDegraded = computed(() => !!signal.value?.degraded)
const degradedPill = computed(() => {
  const r = signal.value?.m_reason
  return r && cfg.degradedPills ? cfg.degradedPills[r] ?? null : null
})

// Momentum in the payload is a decimal fraction (0.0034 == +34 bps); the
// panel presents bps to match threshold_bps.
function momentumBps(src: object | null | undefined): number | null {
  const m = (src as Record<string, unknown> | null | undefined)?.[cfg.momentumField]
  return typeof m === 'number' ? m * 10_000 : null
}
const mBps = computed(() => momentumBps(signal.value))
const thresholdBps = computed(() => signal.value?.threshold_bps ?? null)
const timestampIso = computed(() => signal.value?.timestamp ?? null)

// Live preview strip (2026-08-17): the display-only ticking replica on
// latest:signal:<fund>:preview. Never the decision — the 08:40 stamp above
// stays authoritative; this is what the print WOULD decide if stamped now.
const preview = computed(() => signal.value?.preview ?? null)
const previewStale = computed(() => !!preview.value && (preview.value.m_stale || preview.value.degraded))
const previewMBps = computed(() => momentumBps(preview.value))
const previewDecision = computed(() => preview.value?.decision ?? 'HOLD')
const previewBarAge = computed(() => preview.value?.last_bar_age_min ?? null)

// K_t synthetic NAV (+ optional official NAV leg). kGbp / price are PENCE.
const ktLatest = ref<GoldKtLatestResponse | null>(null)
const navLatest = ref<FundNavLatest | null>(null)
const hasNavLeg = !!cfg.fetchNavLatest
const navFigure = computed(() => {
  const n = navLatest.value
  if (!n?.available || n.price == null) return null
  return { pence: `${n.price.toFixed(2)}p`, date: n.date ?? null }
})
const ktFigure = computed(() => {
  const kt = ktLatest.value
  if (!kt?.available || kt.kGbp == null) return null
  const ret = kt.returnFrac != null
    ? `${kt.returnFrac >= 0 ? '+' : ''}${(kt.returnFrac * 100).toFixed(2)}%`
    : null
  return { pence: `${kt.kGbp.toFixed(2)}p`, ret, date: kt.date ?? null }
})
async function loadNavAndKt(): Promise<void> {
  if (cfg.fetchNavLatest) {
    try { navLatest.value = await cfg.fetchNavLatest() } catch { /* keep last */ }
  }
  try { ktLatest.value = await cfg.fetchKtLatest() } catch { /* keep last */ }
}

// Strike-anchor badge (plan 2026-08-20-strike-anchor-multifund Tasks 22/23):
// its own poll of the same kt-latest endpoint. Ship-gate invariant: never a
// %-figure mixing pre-/post-strike math without a badge; a degraded day
// shows NO % at all (a wrong green number is worse than a visible amber badge).
const { badge: strikeBadge, badgeText: strikeBadgeText, returnPct: strikeReturnPct } =
  useStrikeAnchor(cfg.strikeAnchorFund)
const ktRetDisplay = computed(() => {
  if (strikeBadge.value === 'amber') return null
  if (strikeBadge.value === 'green') {
    const r = strikeReturnPct.value
    return r != null ? `${r >= 0 ? '+' : ''}${r.toFixed(2)}%` : null
  }
  return ktFigure.value?.ret ?? null
})

// Live synth-NAV hero (2026-09-06 live-synth-nav-uniform arcs): the 5s
// anchor_pence × (1 + live_return_gbp) figure, BGA/JH SignalVerdict parity.
// K_t is demoted to the telemetry below and is the feed-down fallback.
const { data: liveNav } = cfg.useLiveSynthNav()
const synthNavPence = computed(() => liveNav.value?.synthNavPence ?? liveNav.value?.anchorPence ?? null)
const synthNavDisplay = computed(() => synthNavPence.value != null ? `${synthNavPence.value.toFixed(0)}p` : '—')
const liveReturnDisplay = computed(() => {
  const r = liveNav.value?.liveReturnGbp
  if (r === undefined || r === null) return '—'
  return `${r >= 0 ? '▲ +' : '▼ '}${Math.abs(r * 100).toFixed(2)}%`
})
const liveReturnClass = computed(() => {
  const r = liveNav.value?.liveReturnGbp
  return r != null && r < 0 ? 'ret-neg' : 'ret-pos'
})
// 'closed' (2026-10-31, paolo): Globex shut — neutral "MARKET CLOSED", the last bar's return kept. Only 'critical'
// (a dead feed while the market trades) is red with the return zeroed.
const tierLabel = computed(() =>
  liveNav.value?.tier === 'closed' ? 'MARKET CLOSED' : (liveNav.value?.tier ?? '').toUpperCase())
// kt-anchor-badge palette: live=green, degraded=amber, critical=red, closed=neutral.
const TIER_BADGE_CLASS: Record<string, string> = {
  live: 'kt-badge-green', degraded: 'kt-badge-amber', critical: 'kt-badge-red', closed: 'kt-badge-neutral',
}
const tierBadgeClass = computed(() => TIER_BADGE_CLASS[liveNav.value?.tier ?? ''] ?? 'kt-badge-neutral')
const coveragePct = computed(() => Math.round((liveNav.value?.coveredWeightFrac ?? 0) * 100))

// Telemetry is collapsible on mobile only (gold Task 8 mobile ruling):
// collapsed under 480px at mount, expanded on desktop; follows viewport
// transitions until the user toggles it, then respects their choice.
const ktOpen = ref(true)
const ktToggledByUser = ref(false)
function onKtToggle(e: Event): void {
  ktOpen.value = (e.target as HTMLDetailsElement).open
  ktToggledByUser.value = true
}
let ktMediaQuery: MediaQueryList | null = null
let ktMediaQueryHandler: ((ev: MediaQueryListEvent) => void) | null = null

// Live overlay bridge (gold): never collapsed with the telemetry.
const overlay = computed(() => cfg.overlay ? cfg.overlay.derive(ktLatest.value) : null)
const overlayBadgeClass = computed(() =>
  overlay.value?.badge === 'ok' ? 'kt-badge-green' : overlay.value?.badge === 'clamped' ? 'kt-badge-amber' : 'kt-badge-neutral')

// (i) explainers — one section open at a time; same (i) closes, another switches.
const explainerSection = ref<string | null>(null)
function openExplainer(key: string) {
  explainerSection.value = explainerSection.value === key ? null : key
}

const decisionReasonText = computed(() => {
  const r = signal.value?.reason
  return r ? (cfg.decisionReasons[r] ?? r) : null
})
const thresholdReasonText = computed(() => {
  const r = signal.value?.threshold_reason
  if (!r) return null
  return r === 'steady'
    ? 'steady threshold (currently invested — reentry hysteresis inactive)'
    : r === 'reentry'
      ? 're-entry threshold (currently flat — higher bar to re-enter)'
      : r
})

const view = computed<FundSignalView>(() => ({
  signal: signal.value,
  mBps: mBps.value,
  thresholdBps: thresholdBps.value,
  thresholdReasonText: thresholdReasonText.value,
  decisionLabel: decisionLabel.value,
  decisionReasonText: decisionReasonText.value,
  ktLatest: ktLatest.value,
  overlay: overlay.value,
}))
const extraStats = computed(() => cfg.extraStats(view.value))

// 09:00 Europe/London HL dealing-cutoff countdown, calendar-aware.
const calendar = ref<CalendarStatus | null>(null)
const { countdown: cutoffCountdown, start: startCountdown, stop: stopCountdown } =
  useHlCutoffCountdown(calendar, cfg.dealingCalendarKey)

async function load(): Promise<void> {
  // Only the FIRST load drives loading/errored — later 30s preview polls
  // keep the last-good payload on transient failures.
  const first = signal.value === null
  if (first) {
    loading.value = true
    errored.value = false
  }
  try {
    signal.value = await cfg.fetchSignal()
    errored.value = false
  } catch {
    if (first) errored.value = true
  } finally {
    if (first) loading.value = false
  }
}

let ktTimer: number | null = null
let signalTimer: number | null = null
onMounted(() => {
  void load()
  void loadNavAndKt()
  ktTimer = window.setInterval(() => { void loadNavAndKt() }, 300_000)
  // The producer republishes the preview every ~60s; 30s keeps the card at
  // most one producer tick behind.
  signalTimer = window.setInterval(() => { void load() }, 30_000)
  // One-shot: the trading-day flag and next day don't change intraday.
  Promise.resolve()
    .then(() => api.calendarToday())
    .then(c => { calendar.value = c ?? null })
    .catch(() => { calendar.value = null })
  startCountdown()
  try {
    if (typeof window.matchMedia === 'function') {
      const mql = window.matchMedia('(max-width: 480px)')
      if (mql.matches) ktOpen.value = false
      ktMediaQuery = mql
      ktMediaQueryHandler = (ev: MediaQueryListEvent) => {
        if (ktToggledByUser.value) return
        ktOpen.value = !ev.matches
      }
      mql.addEventListener('change', ktMediaQueryHandler)
    }
  } catch { /* matchMedia unavailable — keep the expanded default */ }
})
onUnmounted(() => {
  stopCountdown()
  if (ktTimer !== null) window.clearInterval(ktTimer)
  if (signalTimer !== null) window.clearInterval(signalTimer)
  if (ktMediaQuery !== null && ktMediaQueryHandler !== null) {
    try { ktMediaQuery.removeEventListener('change', ktMediaQueryHandler) }
    catch { /* older browsers — cleanup best-effort */ }
  }
})
</script>

<template>
  <div class="fund-signal" :class="cfg.rootClass" :data-testid="`${k}-signal-panel`"
       :style="{ '--fund-accent': cfg.accent, '--fund-explainer-accent': cfg.explainerAccent }">
    <div class="header-row">
      <h2>{{ cfg.title }}</h2>
      <div v-if="cutoffCountdown" class="cutoff" :class="{ passed: cutoffCountdown.passed }">
        {{ cutoffCountdown.label }}
      </div>
    </div>

    <div v-if="loading" class="muted">Loading…</div>

    <div v-else-if="errored" class="muted">
      {{ cfg.errorName }} signal unavailable — API error.
    </div>

    <template v-else>
    <div v-if="!isAvailable" class="muted" :data-testid="`${k}-signal-unavailable`">
      Awaiting today's {{ cfg.awaitName }} decision — the 08:40 London producer has not
      yet published <code>{{ cfg.redisKey }}</code>.
    </div>

    <div v-else class="content">
      <div class="decision-row">
        <span class="decision" :class="'decision-' + decisionLabel.toLowerCase()"
              :data-testid="`${k}-decision`">
          {{ decisionLabel }}
        </span>
        <span v-if="isDegraded" class="badge degraded" :data-testid="`${k}-degraded-badge`">
          DEGRADED — kept prior position: {{ positionProxy ?? 'unknown' }}
        </span>
        <span v-else-if="positionProxy" class="badge proxy">
          position proxy: {{ positionProxy }}
        </span>
        <span v-if="isDegraded && degradedPill" class="kt-anchor-badge kt-badge-amber degraded-pill"
              :data-testid="degradedPill.testid" :title="degradedPill.title">
          {{ degradedPill.text }}
        </span>
      </div>

      <!-- Live synth-NAV hero; falls back to the K_t (or NAV) figure, clearly
           labelled, when the live feed is down. -->
      <div class="nav-hero" :data-test="`${k}-hero-nav`">
        <template v-if="liveNav">
          <span class="nav-value">{{ synthNavDisplay }}</span>
          <span class="nav-return" :class="liveReturnClass">{{ liveReturnDisplay }}</span>
          <span class="tier-badge kt-anchor-badge" :class="tierBadgeClass"
                :data-test="`${k}-hero-tier`">{{ tierLabel }}</span>
          <span class="coverage-badge" :data-test="`${k}-hero-coverage`">coverage {{ coveragePct }}%</span>
        </template>
        <template v-else-if="navFigure || ktFigure">
          <span class="nav-feed-down">
            Live feed down — showing K<sub>t</sub> telemetry
            <strong class="nav-value">{{ ktFigure?.pence ?? navFigure?.pence }}</strong>
          </span>
        </template>
        <template v-else>
          <span class="nav-feed-down muted">Live feed down — awaiting K<sub>t</sub> telemetry</span>
        </template>
      </div>

      <!-- K_t (and optional NAV) telemetry: demoted from the hero, never
           removed. Collapsible on mobile only. -->
      <details class="kt-telemetry" :data-test="`${k}-kt-telemetry`" :open="ktOpen" @toggle="onKtToggle">
        <summary><template v-if="hasNavLeg">NAV &amp; </template>K<sub>t</sub> telemetry</summary>
        <div v-if="navFigure || ktFigure" class="kt-figure" :data-testid="`${k}-${cfg.telemetryTestIdSuffix}`"
             :title="hasNavLeg ? undefined : 'K_t synthetic NAV — daily-close monitoring estimate of the fund price (as of ' + (ktFigure?.date ?? '—') + ').'">
          <template v-if="navFigure">
            NAV <strong>{{ navFigure.pence }}</strong>
            <span class="kt-date">{{ navFigure.date }}</span>
          </template>
          <template v-if="ktFigure">
            <span v-if="hasNavLeg" class="nav-sep">·</span>
            K<sub>t</sub><template v-if="!hasNavLeg"> synth NAV</template> <strong>{{ ktFigure.pence }}</strong>
            <span v-if="strikeBadge !== 'none'" class="kt-anchor-badge"
                  :class="strikeBadge === 'green' ? 'kt-badge-green' : 'kt-badge-amber'"
                  :data-testid="`${k}-kt-anchor-badge`">{{ strikeBadgeText }}</span>
            <span v-if="ktRetDisplay" class="kt-ret" :data-testid="`${k}-kt-ret`">{{ ktRetDisplay }}</span>
            <span class="kt-date">{{ ktFigure.date }}</span>
          </template>
        </div>
        <div v-else class="muted kt-telemetry-empty">K_t telemetry unavailable.</div>
      </details>

      <div v-if="overlay?.active" class="overlay-line" :data-testid="`${k}-overlay-line`">
        <span class="overlay-arrow">└</span> live: {{ cfg.overlay!.assetLabel }} {{ formatOverlayPct(overlay.goldMoveFrac) }}
        × β {{ overlay.beta != null ? overlay.beta.toFixed(2) : '—' }}
        → combined {{ formatOverlayPct(overlay.combinedFrac) }}
        <span class="kt-anchor-badge" :data-testid="`${k}-overlay-badge`" :class="overlayBadgeClass">
          [OVERLAY]
        </span>
        <button class="info-btn" :data-testid="`${k}-info-overlay`" :aria-label="`Explain the ${cfg.overlay!.assetLabel} overlay`"
                @click="openExplainer('overlay')">i</button>
      </div>

      <div class="grid">
        <div class="stat">
          <div class="label">m<sub>{{ cfg.momentumSub }}</sub>
            <button class="info-btn" :data-testid="`${k}-info-m`" :aria-label="`Explain m${cfg.momentumSub}`"
                    @click="openExplainer(cfg.momentumExplainerKey)">i</button>
          </div>
          <div class="value" :data-testid="`${k}-m-bps`">{{ bps(mBps) }}</div>
          <div class="sub">threshold {{ thresholdBps != null ? thresholdBps.toFixed(1) + ' bps' : '—' }}</div>
        </div>
        <div v-for="s in extraStats" :key="s.label" class="stat">
          <!-- A plain-text label keeps its space before the (i); a <sub>
               label sits flush against it (the original panels' markup). -->
          <div class="label">{{ s.label }}{{ s.info && !s.labelSub ? ' ' : '' }}<sub v-if="s.labelSub">{{ s.labelSub }}</sub>
            <button v-if="s.info" class="info-btn" :data-testid="s.info.testid" :aria-label="s.info.aria"
                    @click="openExplainer(s.info.key)">i</button>
          </div>
          <div class="value" :class="s.valueClass" :data-testid="s.valueTestId">{{ s.value }}</div>
          <div class="sub">{{ s.sub }}</div>
        </div>
        <div class="stat">
          <div class="label">Decision at
            <button class="info-btn" :data-testid="`${k}-info-decision`" aria-label="Explain decision"
                    @click="openExplainer('decision')">i</button>
          </div>
          <div class="value">{{ fmtTimestamp(timestampIso) }}</div>
          <div class="sub">Europe/London</div>
        </div>
      </div>

      <div v-if="signal?.reason" class="reason" :data-testid="`${k}-reason`"
           :title="'code: ' + signal.reason">
        <span class="label">reason</span>
        {{ decisionReasonText ?? signal.reason }}
      </div>

      <div v-if="explainerSection" class="explainer" :data-testid="`${k}-explainer`"
           :data-section="explainerSection">
        <component :is="cfg.explainer" :section="explainerSection" :v="view" />
      </div>
    </div>

    <!-- Live preview strip: the ticking display replica; the stamped
         decision above (when present) is the ONLY authoritative signal. -->
    <div v-if="preview" class="preview-strip" :class="{ stale: previewStale }"
         :data-testid="`${k}-preview-strip`">
      <span class="preview-label">
        LIVE PREVIEW <span class="preview-note">— not the decision</span>
      </span>
      <template v-if="!previewStale">
        <span class="preview-decision" :class="'decision-' + previewDecision.toLowerCase()"
              :data-testid="`${k}-preview-decision`">{{ previewDecision }}</span>
        <span class="preview-m" :data-testid="`${k}-preview-m`">{{ bps(previewMBps) }}</span>
        <span v-if="cfg.previewShowsGate" class="preview-gate" :class="preview.gate_open === false ? 'gate-blocked' : 'gate-open'">
          gate {{ gateStateText(preview.gate_open) }}
        </span>
        <span class="preview-meta">
          thr {{ preview.threshold_bps != null ? preview.threshold_bps.toFixed(1) + ' bps' : '—' }}
          · tick {{ fmtTimestamp(preview.timestamp ?? null) }}
          <template v-if="previewBarAge != null">· {{ cfg.futuresSymbol }} bar {{ previewBarAge.toFixed(0) }}m old</template>
        </span>
      </template>
      <template v-else>
        <span class="preview-stale-text" :data-testid="`${k}-preview-stale`">
          STALE — no fresh {{ cfg.futuresSymbol }} print<template v-if="previewBarAge != null">
            (last bar {{ previewBarAge.toFixed(0) }}m old)</template>; preview paused
        </span>
      </template>
    </div>
    </template>
  </div>
</template>

<style scoped>
.kt-figure {
  font-size: 0.82rem;
  color: var(--muted, #9a9aa8);
  display: inline-flex;
  align-items: baseline;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.kt-figure strong { color: var(--fg, #e8e8f0); font-size: 0.95rem; }
.kt-date { opacity: 0.7; font-size: 0.72rem; }
.nav-sep { opacity: 0.5; }
.kt-ret { font-size: 0.78rem; }
/* Strike-anchor / tier / overlay badge palette (ship-gate invariant: a wrong
   green number is worse than a visible amber badge). neutral = confidence
   missing/unrecognised — never green, not the confirmed-clamped amber. */
.kt-anchor-badge {
  padding: 0.05rem 0.35rem;
  border-radius: 0.2rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.kt-badge-amber {
  color: #b45f06;
  background: rgba(255, 175, 60, 0.12);
  border: 1px solid rgba(255, 175, 60, 0.35);
}
.kt-badge-green {
  color: #2f8f4e;
  background: rgba(63, 185, 80, 0.12);
  border: 1px solid rgba(63, 185, 80, 0.35);
}
.kt-badge-neutral {
  color: #888;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.kt-badge-red {
  color: #f85149;
  background: rgba(248, 81, 73, 0.12);
  border: 1px solid rgba(248, 81, 73, 0.35);
}

/* Live synth-NAV hero — BGA/JH SignalVerdict class-name parity. */
.nav-hero {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.6rem;
}
.nav-hero .nav-value { font-size: 1.7rem; font-weight: 700; color: var(--fg, #e8e8f0); line-height: 1.1; }
.nav-hero .nav-return { font-size: 1rem; font-weight: 600; }
.nav-hero .ret-pos { color: #3fb950; }
.nav-hero .ret-neg { color: #f85149; }
.tier-badge { font-size: 0.72rem; }
.coverage-badge {
  font-size: 0.75rem; color: var(--muted, #9a9aa8);
  font-variant-numeric: tabular-nums;
}
.nav-feed-down {
  font-size: 0.85rem; color: #d9822b;
  display: inline-flex; align-items: baseline; gap: 0.4rem; flex-wrap: wrap;
}
.nav-feed-down .nav-value { font-size: 1.1rem; }

/* Telemetry: native details/summary; collapsed under 480px at mount (two-tier
   hero + overlay line risks exceeding ~600px on a phone). */
.kt-telemetry {
  border-top: 1px solid var(--border, #2a2a2a);
  padding-top: 0.5rem;
}
.kt-telemetry summary {
  cursor: pointer;
  font-size: 0.72rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.kt-telemetry summary::marker { color: #666; }
.kt-telemetry .kt-figure,
.kt-telemetry .kt-telemetry-empty {
  margin-top: 0.5rem;
}
@media (max-width: 480px) {
  .kt-telemetry summary { padding: 0.15rem 0; }
  .kt-telemetry .kt-figure,
  .kt-telemetry .kt-telemetry-empty {
    margin-top: 0.35rem;
  }
}

/* Live overlay bridge line (gold) — sibling of the telemetry, never collapsed. */
.overlay-line {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem;
  font-size: 0.78rem; color: var(--muted, #9a9aa8);
}
.overlay-arrow { opacity: 0.6; font-family: monospace; }

.info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.95rem;
  height: 0.95rem;
  margin-left: 0.3rem;
  border: 1px solid var(--border, #4a4a58);
  border-radius: 50%;
  background: transparent;
  color: var(--muted, #9a9aa8);
  font-size: 0.62rem;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;
}
.info-btn:hover { color: var(--fg, #e8e8f0); border-color: var(--fg, #e8e8f0); }
.explainer {
  margin-top: 0.9rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--border, #4a4a58);
  border-left: 3px solid var(--accent, var(--fund-explainer-accent));
  border-radius: 6px;
  font-size: 0.82rem;
  line-height: 1.45;
}
/* The prose is the fund's explainer component (config.explainer). */
.explainer :deep(p) { margin: 0.35rem 0; }
.explainer :deep(.info-now) { opacity: 0.95; }
.explainer :deep(code) { font-size: 0.8rem; }

.fund-signal {
  padding: 1.25rem;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
}
.header-row {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 0.75rem;
}
.fund-signal h2 { margin: 0; font-size: 1rem; color: #eee; }
.cutoff {
  color: var(--fund-accent); font-size: 0.8rem; font-variant-numeric: tabular-nums;
}
.cutoff.passed { color: #888; }
.muted { color: #888; font-size: 0.9rem; }
.content { display: flex; flex-direction: column; gap: 0.85rem; }
.decision-row {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.75rem;
}
.decision {
  font-size: 1.6rem; font-weight: 700; letter-spacing: 0.5px;
  padding: 0.15rem 0.75rem; border-radius: 4px;
}
.decision-buy  { color: #1a1a1a; background: #56d364; }
.decision-sell { color: #fff;    background: #d3555c; }
.decision-hold { color: #eee;    background: #444;    }
.badge {
  font-size: 0.75rem; padding: 0.2rem 0.6rem; border-radius: 3px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.badge.degraded { background: #4a2f00; color: #ffb84d; }
.badge.proxy    { background: #202020; color: #888; }
/* Degraded explainer pill (GGE crude-roll guard) — kt-badge-amber, aligned
   to the other badges' baseline. */
.degraded-pill { display: inline-flex; align-items: center; }
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}
.stat {
  padding: 0.6rem 0.75rem;
  background: #111;
  border-radius: 4px;
}
.label { color: #888; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; }
.value { color: var(--fund-accent); font-size: 1.1rem; font-weight: 600; margin-top: 2px; font-variant-numeric: tabular-nums; }
.value.gate-open    { color: #56d364; }
.value.gate-blocked { color: #d3555c; }
.sub { color: #666; font-size: 0.7rem; margin-top: 2px; }
.reason {
  display: flex; gap: 0.5rem; align-items: center;
  font-size: 0.75rem; color: #888;
}

/* Live preview strip (2026-08-17) */
.preview-strip {
  margin-top: 0.85rem;
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
  padding: 0.5rem 0.75rem;
  background: #111;
  border: 1px dashed #3a3a3a;
  border-radius: 4px;
  font-size: 0.8rem;
}
.preview-strip.stale { opacity: 0.6; }
.preview-label {
  color: #888; font-size: 0.7rem; text-transform: uppercase;
  letter-spacing: 0.5px; font-weight: 600;
}
.preview-note { text-transform: none; letter-spacing: normal; font-weight: 400; }
.preview-decision {
  font-size: 0.95rem; font-weight: 700; letter-spacing: 0.5px;
  padding: 0.05rem 0.5rem; border-radius: 3px;
}
.preview-m { color: var(--fund-accent); font-weight: 600; font-variant-numeric: tabular-nums; }
.preview-gate { font-size: 0.75rem; }
.preview-gate.gate-open { color: #56d364; }
.preview-gate.gate-blocked { color: #d3555c; }
.preview-meta { color: #666; font-size: 0.72rem; font-variant-numeric: tabular-nums; }
.preview-stale-text { color: #999; font-size: 0.78rem; }
</style>
